import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useHomeProjectRedirect } from "@/hooks/useHomeProjectRedirect";
import {
  clearRememberedLoginEmail,
  readRememberedLoginEmail,
  saveRememberedLoginEmail,
} from "@/utils/rememberedLoginEmail";
import { getSignInErrorMessage } from "@/utils/authErrorMessages";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SignInModal: React.FC<SignInModalProps> = ({ isOpen, onClose }) => {
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Hooks
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { performRedirect, isRedirecting } = useHomeProjectRedirect();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const rememberedEmail = readRememberedLoginEmail();
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setPassword("");
      setRememberMe(false);
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await login({ identifier: email, password });
      if (rememberMe) {
        saveRememberedLoginEmail(email);
      } else {
        clearRememberedLoginEmail();
      }
      onClose();
      // Use home project redirect - will go to home project viewer if set, otherwise dashboard
      await performRedirect();
    } catch (err) {
      setError(getSignInErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    onClose();
    navigate("/forgot-password");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="light" className="sm:max-w-[440px] p-0 overflow-hidden">
        {/* Header with icon badge */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50">
              <LogIn className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-gray-900">Sign In</h3>
              <p className="text-sm text-gray-500">
                Enter your credentials to access your workspace
              </p>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100" />

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Error Messages */}
          {error && (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-md text-sm"
            >
              {error}
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <label
              htmlFor="signin-email"
              className="text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <Input
              id="signin-email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="email"
              className="h-10 bg-white border-gray-200 focus-visible:ring-emerald-500"
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <label
              htmlFor="signin-password"
              className="text-sm font-medium text-gray-700"
            >
              Password
            </label>
            <Input
              id="signin-password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="current-password"
              className="h-10 bg-white border-gray-200 focus-visible:ring-emerald-500"
            />
          </div>

          {/* Remember Me + Forgot Password Row */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
                className="w-4 h-4 rounded cursor-pointer border-input accent-primary focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-none"
              />
              <span className="text-sm text-gray-500">
                Remember email
              </span>
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-gray-500 hover:text-emerald-700 hover:underline focus:outline-none focus:underline transition-colors"
            >
              Forgot password?
            </button>
          </div>
        </form>

        {/* Footer with submit button */}
        <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4">
          <Button
            type="submit"
            onClick={handleSubmit}
            className="w-full h-10 font-medium transition-all duration-200 bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={isLoading || isRedirecting || !email || !password}
          >
            {isRedirecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redirecting...
              </>
            ) : isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
